import React, { useState, useEffect, useCallback, useRef } from 'react'
import { warrantiesApi } from '../../services/warranties'
import type { Warranty, WarrantyTranslation } from '../../services/warranties'
import { Toast } from '../shared/Toast'
import { ConfirmDialog } from '../shared/ConfirmDialog'

const WARRANTY_TYPES = [
  { value: 'no_warranty', label: 'No Warranty / As-Is' },
  { value: 'full', label: 'Full Warranty' },
  { value: 'limited', label: 'Limited Warranty' },
]

// Warranty modal with language tabs
function WarrantyModal({ warranty, onSave, onClose }: {
  warranty: Warranty | null
  onSave: (data: { name: string; is_default: boolean; warranty_type: string; labor_percentage: number; parts_percentage: number; translations: WarrantyTranslation[] }) => Promise<void>
  onClose: () => void
}) {
  const [name, setName] = useState(warranty?.name || '')
  const [warrantyType, setWarrantyType] = useState(warranty?.warranty_type || 'full')
  const [laborPct, setLaborPct] = useState(warranty?.labor_percentage ?? 100)
  const [partsPct, setPartsPct] = useState(warranty?.parts_percentage ?? 100)
  const [isDefault, setIsDefault] = useState(warranty?.is_default || false)
  const [activeTab, setActiveTab] = useState<'english' | 'spanish'>('english')
  const [englishContent, setEnglishContent] = useState(
    warranty?.translations.find(t => t.language === 'english')?.content || ''
  )
  const [spanishContent, setSpanishContent] = useState(
    warranty?.translations.find(t => t.language === 'spanish')?.content || ''
  )
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => { nameRef.current?.focus() }, [])

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!name.trim()) newErrors.name = 'Name is required'
    if (!englishContent.trim()) newErrors.english = 'English translation is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    try {
      const translations: WarrantyTranslation[] = []
      const existingEn = warranty?.translations.find(t => t.language === 'english')
      const existingEs = warranty?.translations.find(t => t.language === 'spanish')

      translations.push({ id: existingEn?.id, language: 'english', content: englishContent.trim() })
      if (spanishContent.trim()) {
        translations.push({ id: existingEs?.id, language: 'spanish', content: spanishContent.trim() })
      } else if (existingEs?.id) {
        translations.push({ id: existingEs.id, language: 'spanish', content: '', _destroy: true })
      }

      await onSave({
        name: name.trim(), is_default: isDefault, warranty_type: warrantyType,
        labor_percentage: laborPct, parts_percentage: partsPct, translations,
      })
    } catch {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-secondary">
              {warranty ? 'Edit Warranty' : 'New Warranty'}
            </h3>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="warranty-name" className="block text-sm font-medium text-secondary mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input ref={nameRef} id="warranty-name" type="text" value={name}
                onChange={e => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })) }}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                placeholder="e.g., Full Coverage Warranty" />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* Type + Coverage */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="warranty-type" className="block text-sm font-medium text-secondary mb-1">Type</label>
                <select id="warranty-type" value={warrantyType} onChange={e => setWarrantyType(e.target.value as 'no_warranty' | 'full' | 'limited')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary">
                  {WARRANTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="labor-pct" className="block text-sm font-medium text-secondary mb-1">Labor %</label>
                <input id="labor-pct" type="number" min={0} max={100} value={laborPct}
                  onChange={e => setLaborPct(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              </div>
              <div>
                <label htmlFor="parts-pct" className="block text-sm font-medium text-secondary mb-1">Parts %</label>
                <input id="parts-pct" type="number" min={0} max={100} value={partsPct}
                  onChange={e => setPartsPct(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              </div>
            </div>

            {/* Language Tabs */}
            <div>
              <div className="flex border-b border-gray-200 mb-3">
                <button type="button"
                  onClick={() => setActiveTab('english')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'english' ? 'border-primary text-primary' : 'border-transparent text-grey hover:text-secondary'}`}>
                  English <span className="text-red-500">*</span>
                </button>
                <button type="button"
                  onClick={() => setActiveTab('spanish')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'spanish' ? 'border-primary text-primary' : 'border-transparent text-grey hover:text-secondary'}`}>
                  Spanish
                  {spanishContent.trim() && <span className="ml-1.5 w-1.5 h-1.5 bg-green-500 rounded-full inline-block" />}
                </button>
              </div>

              {activeTab === 'english' ? (
                <div>
                  <textarea id="english-content" value={englishContent}
                    onChange={e => { setEnglishContent(e.target.value); setErrors(prev => ({ ...prev, english: '' })) }}
                    rows={6}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y ${errors.english ? 'border-red-400' : 'border-gray-300'}`}
                    placeholder="Enter the English warranty content..." />
                  {errors.english && <p className="mt-1 text-xs text-red-500">{errors.english}</p>}
                </div>
              ) : (
                <div>
                  <textarea id="spanish-content" value={spanishContent}
                    onChange={e => setSpanishContent(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
                    placeholder="Enter the Spanish warranty content (optional)..." />
                  <p className="mt-1 text-xs text-grey">Spanish translation is optional.</p>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/30" />
              <span className="text-sm text-secondary">Set as default warranty</span>
            </label>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button type="button" onClick={onClose} disabled={saving}
              className="px-4 py-2 text-sm font-medium text-grey border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50">
              {saving ? 'Saving...' : warranty ? 'Update Warranty' : 'Create Warranty'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function WarrantyTemplates(): React.ReactElement {
  const [warranties, setWarranties] = useState<Warranty[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [modalWarranty, setModalWarranty] = useState<Warranty | null | 'new'>(null)
  const [deleteTarget, setDeleteTarget] = useState<Warranty | null>(null)

  const loadWarranties = useCallback(async () => {
    try {
      const data = await warrantiesApi.list()
      setWarranties(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load warranties')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadWarranties() }, [loadWarranties])

  const showToast = (message: string, type: 'success' | 'error') => setToast({ message, type })

  const handleSave = async (data: {
    name: string; is_default: boolean; warranty_type: string;
    labor_percentage: number; parts_percentage: number; translations: WarrantyTranslation[]
  }) => {
    try {
      const params = {
        name: data.name,
        is_default: data.is_default,
        warranty_type: data.warranty_type,
        labor_percentage: data.labor_percentage,
        parts_percentage: data.parts_percentage,
        warranty_translations_attributes: data.translations,
      }

      if (modalWarranty && modalWarranty !== 'new') {
        const updated = await warrantiesApi.update(modalWarranty.id, params)
        setWarranties(prev => {
          if (updated.is_default) {
            return prev.map(w => w.id === updated.id ? updated : { ...w, is_default: false })
          }
          return prev.map(w => w.id === updated.id ? updated : w)
        })
        showToast('Warranty updated', 'success')
      } else {
        const created = await warrantiesApi.create(params)
        setWarranties(prev => {
          if (created.is_default) {
            return [...prev.map(w => ({ ...w, is_default: false })), created]
          }
          return [...prev, created]
        })
        showToast('Warranty created', 'success')
      }
      setModalWarranty(null)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save warranty', 'error')
      throw err
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await warrantiesApi.destroy(deleteTarget.id)
      setWarranties(prev => prev.filter(w => w.id !== deleteTarget.id))
      setDeleteTarget(null)
      showToast('Warranty deleted', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to delete warranty', 'error')
    }
  }

  const typeLabel = (type: string) => WARRANTY_TYPES.find(t => t.value === type)?.label || type

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-grey mt-3">Loading warranties...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-red-600 mb-3">{error}</p>
        <button onClick={loadWarranties} className="text-sm text-primary font-medium hover:underline">Try again</button>
      </div>
    )
  }

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {modalWarranty !== null && (
        <WarrantyModal
          warranty={modalWarranty === 'new' ? null : modalWarranty}
          onSave={handleSave}
          onClose={() => setModalWarranty(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Warranty"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-secondary">Warranty Templates</h2>
            <p className="text-sm text-grey mt-0.5">
              Manage warranty templates with English and Spanish translations.
            </p>
          </div>
          <button onClick={() => setModalWarranty('new')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            New Warranty
          </button>
        </div>

        {warranties.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-secondary mb-1">No warranties yet</h3>
            <p className="text-sm text-grey mb-5 max-w-sm mx-auto">
              Create warranty templates to attach to vehicle listings with multi-language support.
            </p>
            <button onClick={() => setModalWarranty('new')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Create Warranty
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {warranties.map(warranty => (
              <div key={warranty.id} className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all group">
                <div className="flex items-start gap-3 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-secondary truncate">{warranty.name}</h3>
                      {warranty.is_default && (
                        <span className="shrink-0 px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">Default</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-grey">
                      <span className="px-1.5 py-0.5 bg-gray-100 rounded">{typeLabel(warranty.warranty_type)}</span>
                      {warranty.warranty_type !== 'no_warranty' && (
                        <span>Labor {warranty.labor_percentage}% / Parts {warranty.parts_percentage}%</span>
                      )}
                      <span className="flex items-center gap-1">
                        {warranty.translations.map(t => (
                          <span key={t.language} className="px-1.5 py-0.5 bg-secondary/10 text-secondary rounded text-xs font-medium uppercase">
                            {t.language === 'english' ? 'EN' : 'ES'}
                          </span>
                        ))}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setModalWarranty(warranty)}
                      className="p-1.5 text-grey hover:text-secondary hover:bg-gray-100 rounded-lg" title="Edit warranty">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                      </svg>
                    </button>
                    <button onClick={() => setDeleteTarget(warranty)}
                      className="p-1.5 text-grey hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete warranty">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
